import { Link } from "react-router-dom"
import { Navigate, useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import { setState } from "../store/globalSlice"
import errors from "../utils/errors"
import { socket } from "../utils/utils"
import AuthLayout, {
  authInput,
  authMutedText,
  authPrimaryButton,
  authSubtleLink,
} from "../Components/AuthLayout"
import wallpaper from "@/images/backgrounds/wallpaper-forgot-pass.webp"

interface LoginProps {}

const ForgotPassword: React.FC<LoginProps> = () => {
  const dispatch = useAppDispatch()
  const nav = useNavigate()
  const isLoggedIn = useAppSelector((state) => state.global.isLoggedIn)
  const forgetPasswordEmail = useAppSelector(
    (state) => state.global.forgetPasswordEmail
  )

  const onClickOnReset = () => {
    const email = forgetPasswordEmail.trim()
    if (!email) {
      Swal.fire({
        ...errors.auth.allFieldRequired,
        icon: "error",
        heightAuto: false,
      })
      return
    }

    socket.emit("requestResetPass", email)
    // `requestResetPass` has no acknowledgement to wait on — a rejected address
    // comes back as an "error" event that App.tsx puts on screen — so the code
    // screen opens straight away and the error, if there is one, lands on top
    // of it.
    nav("/forgot-pass-otp")
  }

  if (isLoggedIn) {
    return <Navigate to="/lobby"></Navigate>
  }

  return (
    <AuthLayout
      wallpaper={wallpaper}
      marketingTitle="Locked out of the board?"
      marketingBody={
        <>
          <div>
            Give us the email on your account and we'll send a one-time code.
          </div>
          <div>Your properties, trades and stats are exactly where you left them.</div>
        </>
      }
      marketingCta={{ to: "/login", label: "Back to Login" }}
      heading="Forgot password"
      subheading="Enter the email address associated with your Deal Master account."
      footer={
        <div className={`mt-8 text-center sm:mt-4 ${authMutedText}`}>
          <Link to="/login" className={authSubtleLink}>
            Back to login
          </Link>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onClickOnReset()
        }}
        className="flex flex-col gap-4 sm:gap-3"
      >
        <input
          data-testid="email"
          value={forgetPasswordEmail}
          name="email"
          autoComplete="email"
          className={authInput}
          placeholder="Email Address"
          onChange={(e) =>
            dispatch(setState({ forgetPasswordEmail: e.target.value }))
          }
        ></input>

        <button
          type="submit"
          data-testid="forgot-pass-submit-btn"
          className={authPrimaryButton}
        >
          Send reset code
        </button>
      </form>
    </AuthLayout>
  )
}

export default ForgotPassword
